import { useQuery } from '@tanstack/react-query'
import { apiFetch } from './client'

// GET /admin/tool-categories — the platform-managed vocabulary (same as the public
// GET /tool-categories; served under /admin because the production admin load balancer
// only routes /admin, /auth and /health). Each
// connector carries 1–3 of these slugs; flipping a dark connector live requires them.
export interface ToolCategory {
  slug: string
  display_name: string
  sort_order: number
}
export interface ToolCategoriesResponse {
  categories: ToolCategory[]
}

export const MAX_CONNECTOR_CATEGORIES = 3
export const TOOL_CATEGORIES_QUERY_KEY = ['tool-categories'] as const

export function useToolCategories() {
  return useQuery<ToolCategoriesResponse>({
    queryKey: TOOL_CATEGORIES_QUERY_KEY,
    queryFn: () => apiFetch<ToolCategoriesResponse>('/admin/tool-categories'),
    staleTime: 5 * 60_000,
  })
}

// GET /admin/connectors — the Connectors screen. (Upstream MCP records carry their
// connector's `categories` and `exposure` themselves, so the upstream screens do not
// need this lookup; ExposureEditor refreshes both caches after a save.)
export interface AdminConnector {
  id: string
  name: string
  display_name: string | null
  server_type: string | null
  version: string | null
  is_active: boolean
  exposure?: 'dark' | 'live'
  categories?: string[] | null
  created_at: string | null
  tool_count: number
  active_tool_count: number
}
export interface AdminConnectorsResponse {
  connectors: AdminConnector[]
  total: number
}
export const ADMIN_CONNECTORS_QUERY_KEY = ['admin-connectors'] as const

export function useAdminConnectors() {
  return useQuery<AdminConnectorsResponse>({
    queryKey: ADMIN_CONNECTORS_QUERY_KEY,
    queryFn: () => apiFetch<AdminConnectorsResponse>('/admin/connectors'),
  })
}

// PUT /admin/connectors/{connector_ref}/exposure
export interface ExposureRequest {
  exposure: 'dark' | 'live'
  categories?: string[]
}
export interface ExposureResult {
  connector_name: string
  exposure: 'dark' | 'live'
  categories: string[] | null
  changed: boolean
}

export function putExposure(connectorName: string, body: ExposureRequest) {
  return apiFetch<ExposureResult>(
    `/admin/connectors/${encodeURIComponent(connectorName)}/exposure`,
    { method: 'PUT', body: JSON.stringify(body) },
  )
}
